import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import Modal from '../Modal';
import Alert from '../Alert';
import MergeProperties, { mergeSummary, plural } from './MergeProperties';
import { propertiesApi } from '../../requests/api';

const CRITERIA = [
    { id: 'address', label: 'Address', hint: 'St / Street, Ste / Suite, casing, punctuation and spacing are ignored' },
    { id: 'account', label: 'Account #', hint: 'Dashes, spaces and leading zeros are ignored' },
    { id: 'legal_desc', label: 'Legal Description', hint: 'Casing, punctuation and spacing are ignored' },
];

const CRITERIA_LABELS = CRITERIA.reduce((labels, criterion) => {
    labels[criterion.id] = criterion.label;
    return labels;
}, {});

const ALL_CRITERIA = { address: true, account: true, legal_desc: true };

// Long enough to tell two legal descriptions apart, short enough that one row
// does not become a paragraph. The full text is on the cell's title.
const LEGAL_PREVIEW_LENGTH = 160;

function preview(text) {
    const value = (text || '').replace(/\s+/g, ' ').trim();
    return value.length > LEGAL_PREVIEW_LENGTH ? value.slice(0, LEGAL_PREVIEW_LENGTH) + '…' : value;
}

// The API sends dates as yyyy-mm-dd; the list shows them the US way.
function formatDate(value) {
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    return parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : '';
}

export default function FindDuplicateProperties({ open, fetchFilteredSet, filteredCount, onClose }) {
    const [criteria, setCriteria] = React.useState(ALL_CRITERIA);
    const [search, setSearch] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [result, setResult] = React.useState(null);
    // The group whose Merge was clicked, and what the last merge/delete did.
    const [merging, setMerging] = React.useState(null);
    const [notice, setNotice] = React.useState(null);
    // Ids resolved on this screen, so a group the user has dealt with drops
    // out without re-running the whole comparison.
    const [resolvedIds, setResolvedIds] = React.useState([]);
    const [deletingId, setDeletingId] = React.useState(null);
    // Which search the current result answers, so a stale response cannot
    // overwrite a newer one.
    const requestRef = React.useRef(0);
    // The filtered list's ids, fetched once per opening of the popup.
    const filteredIdsRef = React.useRef(null);

    const run = (chosenCriteria, chosenSearch, page) => {
        const criteriaIds = CRITERIA.map((c) => c.id).filter((id) => chosenCriteria[id]);

        if (!criteriaIds.length) {
            setError('Pick at least one field to match on.');
            return;
        }

        const requestId = ++requestRef.current;
        setBusy(true);
        setError(null);

        // Only the properties the list's filter matches are compared, so a
        // territory or an agent's list is searched on its own rather than
        // against all of iCLIMB. The ids are fetched once and reused for
        // paging, so stepping through pages does not re-run the list query.
        const idsReady = filteredIdsRef.current
            ? Promise.resolve(filteredIdsRef.current)
            : fetchFilteredSet().then((rows) => {
                const found = (rows || [])
                    .map((row) => row && row.ARCIMEDES_ROW_ID)
                    .filter(Boolean)
                    .map(Number);
                filteredIdsRef.current = found;
                return found;
            });

        idsReady
            .then((propertyIds) => {
                if (requestId !== requestRef.current) return null;

                if (!propertyIds.length) {
                    throw new Error('This list has no properties to compare.');
                }

                return propertiesApi.findDuplicates(criteriaIds, chosenSearch, page, propertyIds);
            })
            .then((data) => {
                if (requestId !== requestRef.current || data === null) return;
                setBusy(false);
                setResult(data);
                setResolvedIds([]);
            })
            .catch((err) => {
                if (requestId !== requestRef.current) return;
                setBusy(false);
                setError(err.message || 'Could not look for duplicates.');
            });
    };

    // A fresh popup every time it opens. The search itself waits for the
    // button: comparing every property in iCLIMB is not something to start
    // because somebody opened the window.
    React.useEffect(() => {
        if (!open) {
            requestRef.current++;
            return;
        }
        setCriteria(ALL_CRITERIA);
        setSearch('');
        setError(null);
        setResult(null);
        setNotice(null);
        setMerging(null);
        setResolvedIds([]);
        // The filter may have changed since last time.
        filteredIdsRef.current = null;
    }, [open]);

    if (!open) return null;

    const chosenCount = CRITERIA.filter((c) => criteria[c.id]).length;

    const submit = (event) => {
        if (event) event.preventDefault();
        // A new search always starts at the first page.
        run(criteria, search, 1);
    };

    const goToPage = (page) => run(criteria, search, page);

    // A merged-away or deleted property is gone from the data the popup is
    // showing, so its row and any group left with fewer than two records drop
    // out — without re-running a comparison of every property in iCLIMB.
    const resolve = (ids) => setResolvedIds(resolvedIds.concat(ids.map(Number)));

    const onMerged = (data) => {
        setMerging(null);
        resolve(data.merged_ids);
        setNotice(mergeSummary(data));
    };

    const deleteProperty = (property) => {
        if (!window.confirm(`Delete property ${property.id}${property.name ? ` (${property.name})` : ''}?`)) return;

        setDeletingId(property.id);
        setError(null);

        propertiesApi.deleteDuplicates([property.id])
            .then(() => {
                setDeletingId(null);
                resolve([property.id]);
                setNotice(`Deleted property ${property.id}.`);
            })
            .catch((err) => {
                setDeletingId(null);
                setError(err.message || 'Could not delete that property.');
            });
    };

    // Groups as they stand after this screen's merges and deletes.
    const visibleGroups = result
        ? result.groups
            .map((group) => ({
                ...group,
                properties: group.properties.filter((p) => resolvedIds.indexOf(p.id) === -1),
            }))
            .filter((group) => group.properties.length > 1)
        : [];

    const duplicateCount = visibleGroups.reduce((sum, group) => sum + group.properties.length, 0);

    if (merging) {
        return (
            <MergeProperties
                open
                properties={merging.properties}
                onClose={() => setMerging(null)}
                onMerged={onMerged}
            />
        );
    }

    return (
        <Modal
            show={open}
            onClose={busy ? () => {} : onClose}
            className={`FindDups ${visibleGroups.length ? 'has-results' : ''}`}
        >
            <div className="FindDups_body">
                <h2 className="FindDups_title">Find Duplicates</h2>
                <p className="FindDups_intro">
                    Identical properties in your current filtered search are compared based on your
                    selected match on criteria
                </p>

                <form className="FindDups_options" onSubmit={submit}>
                    <fieldset className="FindDups_fieldset">
                        <legend>Match on</legend>
                        <div className="FindDups_checks">
                            {CRITERIA.map((criterion) => (
                                <label key={criterion.id} className="FindDups_option" title={criterion.hint}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(criteria[criterion.id])}
                                        disabled={busy}
                                        onChange={(event) =>
                                            setCriteria({ ...criteria, [criterion.id]: event.target.checked })}
                                    />
                                    <span>{criterion.label}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <fieldset className="FindDups_fieldset FindDups_fieldset--grow">
                        <legend>Only groups mentioning</legend>
                        <input
                            type="text"
                            className="FindDups_search"
                            value={search}
                            disabled={busy}
                            placeholder="Name, address, city, ZIP, account # or legal description"
                            aria-label="Only groups mentioning"
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </fieldset>

                    <button
                        type="submit"
                        className={`button-new positive FindDups_submit ${busy || !chosenCount ? 'inactive' : ''}`}
                        disabled={busy || !chosenCount}
                    >
                        {busy ? 'Searching…' : result ? 'Search again' : 'Find Duplicates'}
                    </button>
                </form>

                {error ? <Alert type="error">{error}</Alert> : null}
                {notice ? <Alert type="success">{notice}</Alert> : null}

                {result ? (
                    <div className="FindDups_results">
                        <p className="FindDups_summary">
                            {result.group_count
                                ? `Found ${plural(result.group_count, 'group')} of duplicates, among ${plural(result.scanned, 'property', 'properties')} compared.`
                                  + (result.last_page > 1
                                      ? ` Showing page ${result.page.toLocaleString()} of ${result.last_page.toLocaleString()}.`
                                      : ` Covering ${plural(duplicateCount, 'property', 'properties')}.`)
                                : `No duplicates among the ${plural(result.scanned, 'property', 'properties')} compared.`}
                        </p>

                        <div className="FindDups_groups">
                            {visibleGroups.map((group, index) => (
                                <div className="FindDups_group" key={group.properties.map((p) => p.id).join('-')}>
                                    <div className="FindDups_group_head">
                                        <span className="FindDups_group_count">
                                            {`Group ${(((result.page || 1) - 1) * (result.per_page || 0) + index + 1).toLocaleString()} · ${plural(group.properties.length, 'property', 'properties')}`}
                                        </span>
                                        <span className="FindDups_group_match">
                                            {'Matched on ' + group.matched_on.map((id) => CRITERIA_LABELS[id] || id).join(', ')}
                                        </span>
                                        <button
                                            type="button"
                                            className="button-new positive FindDups_group_merge"
                                            onClick={() => setMerging(group)}
                                        >
                                            Merge
                                        </button>
                                    </div>
                                    <table className="FindDups_table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Address</th>
                                                <th>City / ZIP</th>
                                                <th>Account #</th>
                                                <th>Legal Description</th>
                                                <th>Source</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.properties.map((property) => (
                                                <tr key={property.id}>
                                                    <td>
                                                        <Link to={`/property/${property.id}`} target="_blank">
                                                            {property.id}
                                                        </Link>
                                                    </td>
                                                    <td>{property.name || ''}</td>
                                                    <td>{property.address || ''}</td>
                                                    <td>
                                                        {[property.city, property.state].filter(Boolean).join(', ')}
                                                        {property.zip ? ` ${property.zip}` : ''}
                                                    </td>
                                                    <td>{property.account_number || ''}</td>
                                                    <td title={property.legal_desc || ''}>{preview(property.legal_desc)}</td>
                                                    <td>{property.source || ''}</td>
                                                    <td>{property.status || ''}</td>
                                                    <td
                                                        className="FindDups_date"
                                                        title={property.status === 'Closed' ? 'Close of escrow' : 'Last edited'}
                                                    >
                                                        {property.status_date ? (
                                                            <React.Fragment>
                                                                <span>{formatDate(property.status_date)}</span>
                                                                <span className="FindDups_date_kind">
                                                                    {property.status === 'Closed' ? '(COE)' : '(Edited)'}
                                                                </span>
                                                            </React.Fragment>
                                                        ) : ''}
                                                    </td>
                                                    <td className="FindDups_row_action">
                                                        <button
                                                            type="button"
                                                            className={`button-new text ${deletingId === property.id ? 'inactive' : ''}`}
                                                            onClick={() => deleteProperty(property)}
                                                            disabled={deletingId === property.id}
                                                            title={`Delete property ${property.id}`}
                                                        >
                                                            {deletingId === property.id ? 'Deleting…' : 'Delete'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>

                        {result.last_page > 1 ? (
                            <div className="FindDups_pager">
                                <button
                                    type="button"
                                    className={`button-new text ${busy || result.page <= 1 ? 'inactive' : ''}`}
                                    onClick={() => goToPage(result.page - 1)}
                                    disabled={busy || result.page <= 1}
                                >
                                    Previous
                                </button>
                                <span className="FindDups_pager_label">
                                    {`Page ${result.page.toLocaleString()} of ${result.last_page.toLocaleString()}`}
                                </span>
                                <button
                                    type="button"
                                    className={`button-new text ${busy || result.page >= result.last_page ? 'inactive' : ''}`}
                                    onClick={() => goToPage(result.page + 1)}
                                    disabled={busy || result.page >= result.last_page}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <p className="FindDups_summary">
                        {busy
                            ? 'Comparing the properties in this list…'
                            : `Choose what to match on, then search. ${
                                filteredCount
                                    ? `The ${Number(filteredCount).toLocaleString()} properties in this list are compared.`
                                    : 'The properties in this list are compared.'
                            } Summit and Peak properties are left out.`}
                    </p>
                )}

                <div className="FindDups_actions">
                    <button
                        type="button"
                        className={`button-new text ${busy ? 'inactive' : ''}`}
                        onClick={onClose}
                        disabled={busy}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
}

FindDuplicateProperties.propTypes = {
    open: PropTypes.bool.isRequired,
    // Resolves to every row the list's current filter matches.
    fetchFilteredSet: PropTypes.func.isRequired,
    // How many rows that filter matches, for the summary line.
    filteredCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClose: PropTypes.func.isRequired,
};
